import React, { useState, useEffect, useCallback } from "react";
import { auth, db } from "../../firebase";
import { sendPasswordResetEmail, createUserWithEmailAndPassword } from "firebase/auth";
import { collection, getDocs, doc, setDoc, getDoc, updateDoc, deleteDoc } from "firebase/firestore";
import * as XLSX from "xlsx";
import { Security as SecurityIcon } from '@mui/icons-material';
import { Box, Button, TextField, Paper, Typography, Grid, Alert, MenuItem, Select, InputLabel, FormControl, TableContainer} from '@mui/material';

import { Tooltip } from '@mui/material';

const registerUser = async (email, password, role) => {
  try {
    // ✅ 1. Check if the current user is an admin before creating a new user
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.error("❌ No authenticated user.");
      return;
    }

    const currentUserRef = doc(db, "users", currentUser.uid);
    const currentUserSnap = await getDoc(currentUserRef);

    if (!currentUserSnap.exists() || !["admin", "superadmin"].includes(currentUserSnap.data().role)) {
      console.error("❌ Permission denied: Only admins can create users.");
      return;
    }

    // ✅ 2. Create user in Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    console.log("✅ User created:", user.uid);

    // ✅ 3. Store the new user in Firestore
    const userRef = doc(db, "users", user.uid);
    await setDoc(userRef, {
      email: email,
      role: role,
      createdAt: new Date().toISOString(),
    });

    console.log("✅ User added to Firestore:", email);
  } catch (error) {
    console.error("❌ Error registering user:", error.code, error.message);
  }
};



function Settings() {
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");
  const [users, setUsers] = useState([]);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPassword, setNewUserPassword] = useState("");
  const [newUserRole, setNewUserRole] = useState("editor");
  const [userRole, setUserRole] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedUserRole, setSelectedUserRole] = useState("");


  const fetchUsers = useCallback(async () => {
    const usersCollection = collection(db, "users");
    const snapshot = await getDocs(usersCollection);
    const usersList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setUsers(usersList);
  }, []);

  useEffect(() => {
    const fetchUserRole = async () => {
      const user = auth.currentUser;
      if (user) {
        const usersCollection = collection(db, "users");
        const snapshot = await getDocs(usersCollection);
        const currentUser = snapshot.docs.find(doc => doc.id === user.uid);
        if (currentUser) {
          setUserRole(currentUser.data().role);
        }
      }
    };
    fetchUserRole();
    fetchUsers();
  }, [fetchUsers]);

  const handleResetPassword = async () => {
    if (!email) {
      setMessage("Please enter your email.");
      setSeverity("warning");
      return;
    }
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage("Password reset link sent! Check your email.");
      setSeverity("success");
    } catch (error) {
      setMessage("Error resetting password: " + error.message);
      setSeverity("error");
    }
  };

  const handleAddUser = async () => {
    if (!newUserEmail || !newUserPassword) {
      setMessage("Please enter all user details.");
      setSeverity("warning");
      return;
    }
    try {
      await registerUser(newUserEmail, newUserPassword, newUserRole);
      setMessage("User added successfully!");
      setSeverity("success");
      fetchUsers();
    } catch (error) {
      setMessage("Error adding user: " + error.message);
      setSeverity("error");
    }
  };

  const filterDataByDate = (data) => {
    if (!startDate || !endDate) return data;

    return data.filter((shipment) => {
      const shipmentDate = new Date(shipment["Intake Date (MM/DD/YYYY)"]);
      return shipmentDate >= new Date(startDate) && shipmentDate <= new Date(endDate);
    });
  };

  const handleDownloadData = async (format) => {
    if (userRole !== "admin") {
      setMessage("You do not have permission to download data.");
      setSeverity("error");
      return;
    }
  
    const shipmentsCollection = collection(db, "shipments");
    const snapshot = await getDocs(shipmentsCollection);
    const data = snapshot.docs.map(doc => doc.data());
    const filteredData = filterDataByDate(data);
  
    if (filteredData.length === 0) {
      alert("No data available for the selected date range.");
      return;
    }
  
    // Define the required column order
    const columnOrder = [
      "Year",
      "ID Number",
      "Intake Date (MM/DD/YYYY)",
      "Collection Point",
      "Category",
      "Subcategory",
      "Specific Food Item",
      "Quantity (whole item)",
      "Quantity (half item)",
      "Wet Weight (g)",
      "Wet Weight (lbs)",
      "Quality",
      "Destination",
      "Processing Method",
      "Dehydration Start Date",
      "Dehydration Temperature",
      "Dehydration Time Interval",
      "Dehydration End Date",
      "Total Dehydration Time (hours)",
      "Dry Weight-194 (g)",
      "Dry Weight-194 (lbs)",
      "Grinding Date",
      "Grinding Total Time (mins)",
      "Powder Weight (g)",
      "Powder Weight (lbs)"
    ];
  
    // Reformat data to match the required column order
    const formattedData = filteredData.map(shipment => {
      let formattedEntry = {};
      columnOrder.forEach(column => {
        formattedEntry[column] = shipment[column] || ""; // Ensure all fields exist
      });
      return formattedEntry;
    });
  
    // Convert JSON to Excel format
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Shipments");
  
    // Download file in the selected format
    if (format === "csv") {
      XLSX.writeFile(workbook, "Shipments_Report.csv");
    } else {
      XLSX.writeFile(workbook, "Shipments_Report.xlsx");
    }
  };
  

  // Delete User (Superadmin Only)
  const handleDeleteUser = async () => {
    if (userRole !== "superadmin") {
      alert("Only superadmins can delete users.");
      return;
    }
    if (!selectedUser) {
      alert("Please select a user to delete.");
      return;
    }
    await deleteDoc(doc(db, "users", selectedUser));
    setUsers(users.filter(user => user.id !== selectedUser));
    setSelectedUser("");
    alert("User deleted successfully.");
  };

  // Change User Role (Admin & Superadmin)
  const handleChangeUserRole = async (userId, newRole) => {
    if (!["admin", "superadmin"].includes(userRole)) {
      alert("Only admins and superadmins can change user roles.");
      return;
    }
    const userRef = doc(db, "users", userId);
    await updateDoc(userRef, { role: newRole });
    setUsers(users.map(user => (user.id === userId ? { ...user, role: newRole } : user)));
    alert("User role updated.");
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <SecurityIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h7">Settings</Typography>
          </Box>
        </Grid>

        {/* Reset Password Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6">Reset Password</Typography>
            <TextField fullWidth label="Email Address" type="email" value={email} onChange={(e) => setEmail(e.target.value)} sx={{ mb: 2 }} />
            <Button variant="contained" onClick={handleResetPassword}>Send Reset Link</Button>
            {message && <Alert severity={severity} sx={{ mt: 2 }}>{message}</Alert>}
          </Paper>
        </Grid>

        {/* Data Export Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6">Download Reports</Typography>
            <TextField fullWidth type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} sx={{ mb: 2 }} />
            <TextField fullWidth type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} sx={{ mb: 2 }} />
            <Button variant="contained" onClick={() => handleDownloadData("xlsx")} sx={{ mr: 2 }}>Download Excel</Button>
            <Button variant="outlined" onClick={() => handleDownloadData("csv")}>Download CSV</Button>
          </Paper>
        </Grid>

        {/* User Management Section (Admins & Superadmins) */}
        {["admin", "superadmin"].includes(userRole) && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6">User Management</Typography>
              <TableContainer>
              <TextField fullWidth label="New User Email" type="email" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} sx={{ mb: 2 }} />
              <TextField fullWidth label="Password" type="password" value={newUserPassword} onChange={(e) => setNewUserPassword(e.target.value)} sx={{ mb: 2 }} />
              <Tooltip title="SuperAdmin/Admin: Full access, Editor: Limited access, Supplier: Access to shipments" arrow>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Role</InputLabel>
                  <Select value={newUserRole} onChange={(e) => setNewUserRole(e.target.value)}>
                    <MenuItem value="superadmin">Superadmin</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="editor">Editor</MenuItem>
                    <MenuItem value="supplier">Supplier</MenuItem>
                  </Select>
                </FormControl>
              </Tooltip>
              <Button variant="contained" onClick={handleAddUser}>Add User</Button>
                 {/* Delete User Section (Superadmin Only) */}
        {userRole === "superadmin" && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6">Delete User</Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select User</InputLabel>
                <Select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
                  {users.map(user => (
                    <MenuItem key={user.id} value={user.id}>{user.email}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button variant="contained" color="error" onClick={handleDeleteUser}>
                Delete User
              </Button>
            </Paper>
          </Grid>
        )}

        {/* Change User Role Section (Admin & Superadmin) */}
        {["admin", "superadmin"].includes(userRole) && (
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6">Change User Role</Typography>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select User</InputLabel>
                <Select value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
                  {users.map(user => (
                    <MenuItem key={user.id} value={user.id}>{user.email}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Role</InputLabel>
                <Select value={selectedUserRole} onChange={(e) => setSelectedUserRole(e.target.value)}>
                  <MenuItem value="superadmin">Superadmin</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="editor">Editor</MenuItem>
                  <MenuItem value="supplier">Supplier</MenuItem>
                </Select>
              </FormControl>
              <Button variant="contained" onClick={handleChangeUserRole}>
                Update Role
              </Button>
            </Paper>
          </Grid>
        )}
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default Settings;
